import { useState } from 'react';
import { DsButton } from '@/modules/internal-v2/ds';
import {
  useAnnouncements,
  useDeleteAnnouncement,
} from '@/api/announcements/announcements.hooks';
import { Announcement } from '@/api/announcements/announcements.types';
import { ContainedPage } from '@/modules/internal-v2/shared/contained-page.component';
import { AnnouncementEditor } from './components/announcement-editor.component';
import { MaintenanceCard } from './components/maintenance-card.component';
import './announcements.css';

interface EditorState {
  open: boolean;
  announcement: Announcement | null;
}

const VARIANT_LABEL: Record<string, string> = {
  INFO: 'Info',
  SUCCESS: 'Erfolg',
  WARNING: 'Warnung',
  ERROR: 'Fehler',
};

const DISPLAY_LABEL: Record<string, string> = {
  BANNER: 'Banner',
  POPUP: 'Popup',
  BOTH: 'Beides',
};

export function AnnouncementsPage() {
  const { data: announcements, isLoading, isError } = useAnnouncements();
  const deleteAnnouncement = useDeleteAnnouncement();
  const [editor, setEditor] = useState<EditorState>({
    open: false,
    announcement: null,
  });

  const handleDelete = (announcement: Announcement) => {
    if (window.confirm(`Ankündigung "${announcement.title}" wirklich löschen?`)) {
      deleteAnnouncement.mutate(announcement.id);
    }
  };

  return (
    <ContainedPage
      toolbar={
        <div className="ann-toolbar-content">
          <h1 className="ann-title">Ankündigungen & Wartung</h1>
          <span className="ann-count">
            {announcements ? `${announcements.length} Ankündigungen` : ''}
          </span>
          <DsButton
            variant="primary"
            size="small"
            onClick={() => setEditor({ open: true, announcement: null })}
          >
            + Neue Ankündigung
          </DsButton>
        </div>
      }
      contained
    >
      <div className="ann-section">
        <MaintenanceCard />

        <div className="ann-card">
          <div className="ann-card__head">
            <span className="ann-card__title">Ankündigungen</span>
          </div>

          {isLoading && <div className="ann-empty">Lädt…</div>}
          {isError && (
            <div className="ann-empty">
              Ankündigungen konnten nicht geladen werden.
            </div>
          )}
          {announcements && announcements.length === 0 && (
            <div className="ann-empty">
              Noch keine Ankündigungen. Lege die erste an.
            </div>
          )}
          {announcements && announcements.length > 0 && (
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Stil</th>
                  <th>Darstellung</th>
                  <th>Zielgruppe</th>
                  <th>Status</th>
                  <th className="num">Priorität</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {announcements.map((a) => (
                  <tr
                    key={a.id}
                    className="ann-row"
                    onClick={() =>
                      setEditor({ open: true, announcement: a })
                    }
                  >
                    <td>{a.title}</td>
                    <td>{VARIANT_LABEL[a.variant] ?? a.variant}</td>
                    <td>{DISPLAY_LABEL[a.displayMode] ?? a.displayMode}</td>
                    <td>{a.audience}</td>
                    <td>
                      <span className={`ds-pill ${a.isActive ? 'pos' : 'muted'}`}>
                        {a.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="num">{a.priority}</td>
                    <td className="ann-actions">
                      <DsButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditor({ open: true, announcement: a });
                        }}
                      >
                        Bearbeiten
                      </DsButton>
                      <DsButton
                        size="small"
                        disabled={deleteAnnouncement.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(a);
                        }}
                      >
                        Löschen
                      </DsButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editor.open && (
        <AnnouncementEditor
          announcement={editor.announcement}
          onClose={() => setEditor({ open: false, announcement: null })}
        />
      )}
    </ContainedPage>
  );
}
